import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import useAuth from "../../hooks/useAuth";
import { FAVOURITE_KINDS, favouriteId } from "../../data/favourites";
import {
  addFavourite,
  listFavourites,
  removeFavourite,
} from "../../services/favourites/favouritesService";

/**
 * The clubs and players this person follows, shared across the app.
 *
 * A context rather than a per-screen hook because two very different
 * places need the same answer: the Teams tab, which sorts by it, and the
 * live game view, which asks "is this one of theirs?" of every play. The
 * game view asking that question must not cost a read per play, so the set
 * is fetched once and kept in memory.
 *
 * Signed out, this is simply empty. Nothing here throws for a missing uid,
 * because browse mode renders the same screens with no account behind
 * them and a star that quietly does nothing is better than a crash.
 */
/*
 * The default is a real, empty value rather than null.
 *
 * Most contexts here throw when used outside their provider, which catches
 * wiring mistakes. This one deliberately does not, because "no favourites"
 * is a legitimate state the app already has to render: a browsing visitor
 * with no account has exactly this. Components like the live game panel
 * are also rendered in isolation by tests and by the style guide, and
 * making every one of those set up an auth provider to show a play feed
 * would be a lot of ceremony to protect against a mistake that shows up
 * immediately as a missing star.
 */
const EMPTY = Object.freeze({
  teams: [],
  players: [],
  teamIds: [],
  playerIds: [],
  loading: false,
  error: null,
  isFavourite: () => false,
  toggle: async () => ({ ok: false, reason: "no-provider" }),
  reload: async () => {},
  canFavourite: false,
});

export const FavouritesContext = createContext(EMPTY);

export function FavouritesProvider({ children }) {
  const { authUser } = useAuth();
  const uid = authUser?.uid ?? null;

  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(Boolean(uid));
  const [error, setError] = useState(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!uid) {
      setTeams([]);
      setPlayers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const grouped = await listFavourites(uid);
      if (!mounted.current) return;
      setTeams(grouped.teams);
      setPlayers(grouped.players);
      setError(null);
    } catch (caught) {
      if (mounted.current) setError(caught);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  /*
   * Membership as a Set of ids.
   *
   * The play feed asks "is this player followed?" for every play in a
   * drive, so this needs to be a lookup and not a scan. Rebuilt only when
   * the lists change.
   */
  const starredIds = useMemo(() => {
    const ids = new Set();
    teams.forEach((f) => ids.add(f.favouriteId));
    players.forEach((f) => ids.add(f.favouriteId));
    return ids;
  }, [teams, players]);

  const isFavourite = useCallback(
    (kind, refId) => starredIds.has(favouriteId(kind, refId)),
    [starredIds]
  );

  /*
   * Optimistic, then reconciled.
   *
   * A star is a tap on a list someone is scanning, so it has to respond
   * now. If the write fails the list is reloaded from the backend, which
   * puts the star back where the stored truth says it belongs rather than
   * leaving the UI asserting something that was never saved.
   */
  const toggle = useCallback(
    async (kind, refId) => {
      if (!uid) return { ok: false, reason: "signed-out" };

      const id = favouriteId(kind, refId);
      const starred = starredIds.has(id);
      const setList = kind === FAVOURITE_KINDS.TEAM ? setTeams : setPlayers;

      if (starred) {
        setList((list) => list.filter((f) => f.favouriteId !== id));
        try {
          await removeFavourite(uid, kind, refId);
          return { ok: true, starred: false };
        } catch (caught) {
          await load();
          return { ok: false, reason: "write-failed", error: caught };
        }
      }

      const optimistic = { favouriteId: id, kind, refId, addedAt: Date.now() };
      setList((list) => [optimistic, ...list]);
      try {
        await addFavourite(uid, kind, refId);
        return { ok: true, starred: true };
      } catch (caught) {
        await load();
        return {
          ok: false,
          reason: caught?.name === "FavouriteLimitReached" ? "limit" : "write-failed",
          error: caught,
        };
      }
    },
    [uid, starredIds, load]
  );

  const value = useMemo(
    () => ({
      teams,
      players,
      teamIds: teams.map((f) => f.refId),
      playerIds: players.map((f) => f.refId),
      loading,
      error,
      isFavourite,
      toggle,
      reload: load,
      canFavourite: Boolean(uid),
    }),
    [teams, players, loading, error, isFavourite, toggle, load, uid]
  );

  return (
    <FavouritesContext.Provider value={value}>
      {children}
    </FavouritesContext.Provider>
  );
}
